import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import get from 'lodash/get';
import Swiper from 'react-id-swiper';
import Modal from '../../../components/Modal/Modal';
import CloseButton from '../../../components/CloseButton/CloseButton';
import { rigthKey, leftKey } from '../../../constants';
import getGallerySwiperParams from '../../../galleryParams';
import { isVideoPosterReady, isImagePosterReady } from '../../../utils/normalizeMediaStatus';
import getVideoSourceFormats from '../../../utils/getVideoSourceFormats';
import resetSwiperZoom from '../../../utils/resetSwiperZoom';
import withPageZoom from '../../PageZoomContainer/PageZoomContainer';
import VideoStatusBadge from '../../../components/VideoStatusBadge/VideoStatusBadge';
import ImageStatusBadge from '../../../components/ImageStatusBadge/ImageStatusBadge';
import * as styles from './OMLightbox.scss';
import iconPdf from '../../../../static/icons/icon-pdf.png';

class OMLightbox extends Component {
  state = {
    currentImage: this.props.currentImage,
    right: 0
  };

  componentDidMount() {
    this.calculateSizes();
    window.addEventListener('resize', this.calculateSizes);
    document.body.addEventListener('keydown', this.handleKeyDown);
    document.body.addEventListener('click', this.bodyClickHandler);
  }

  componentWillReceiveProps({ currentImage }) {
    if (currentImage !== this.props.currentImage || currentImage !== this.state.currentImage) {
      this.setState({ currentImage });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateSizes);
    document.body.removeEventListener('keydown', this.handleKeyDown);
    document.body.removeEventListener('click', this.bodyClickHandler);
  }

  calculateSizes = () => {
    const { isMobile } = this.props;
    const imagesLength = get(this.props, 'images.length') || 0;
    const videosLength = get(this.props, 'videos.length') || 0;

    this.width = document.documentElement.clientWidth;
    this.thumbnailWidth = 121;
    this.sideIndent = (isMobile) ? 18 : 20;
    this.thumbnailsWidth = (isMobile) ? this.width * 0.894 : this.width * 0.53;
    this.fullWidth = this.thumbnailWidth + this.sideIndent;
    this.thumbnailsLength = ((imagesLength + videosLength) * this.fullWidth) - this.sideIndent;
    this.thumbnailCenter = this.thumbnailsWidth / 2;
    this.maxRight = this.thumbnailsLength - this.thumbnailsWidth;
  };

  handleKeyDown = (evt) => {
    if (this.swiperRef) {
      switch (evt.keyCode) {
        case leftKey:
          return this.props.isRtl ? this.swiperRef.swiper.slideNext() : this.swiperRef.swiper.slidePrev();
        case rigthKey:
          return this.props.isRtl ? this.swiperRef.swiper.slidePrev() : this.swiperRef.swiper.slideNext();
        default:
          return false;
      }
    }

    return false;
  };

  handleThumbnailClick = (idx) => {
    const right = this.calculateThumbnailTransition(idx);
    this.swiperRef.swiper.slideTo(idx);
    this.setState({ currentImage: idx, right });
  };

  calculateThumbnailTransition(idx) {
    const direction = this.props.isRtl ? '' : '-';

    let offset = 0;
    if (this.thumbnailsLength > this.thumbnailsWidth) {
      const centerPosition = (idx * this.fullWidth) + (this.thumbnailWidth / 2);
      if (centerPosition < this.thumbnailCenter) {
        offset = 0;
      } else if (centerPosition > (this.thumbnailsLength - this.thumbnailCenter)) {
        offset = this.maxRight;
      } else {
        offset = centerPosition - this.thumbnailCenter;
      }
      this.scrollableAria.style.transform = `translateX(${direction}${offset}px)`;
    }
    return offset;
  }

  bodyClickHandler = (evt) => {
    if (evt.target.classList.length && this.props.isOpen) {
      const target = evt.target.classList[0].split('_')[0];
      switch (target) {
        case 'fa':
        case 'image':
        case 'icon':
          return;

        default:
          this.props.hideLightbox();
      }
    }
  };

  slideChange = () => {
    if (this.swiperRef) {
      this.swiperRef.swiper?.el?.querySelectorAll('video').forEach(v => v.pause());
      const index = this.swiperRef.swiper.activeIndex;
      this.calculateThumbnailTransition(index);
      this.setState({ currentImage: index });
      this.props.changeLightboxActiveSlide && this.props.changeLightboxActiveSlide(index);
    }
  };

  resetZoom = () => resetSwiperZoom(this.swiperRef && this.swiperRef.swiper);

  renderPagination = () => {
    const data = [...this.props.images, ...(this.props.videos || [])];
    const { description } = data[this.state.currentImage] || {};

    return (
      <div className={styles.content}>
        <div>{`${this.state.currentImage + 1} / ${data.length}`}</div>
        { (description) ? <div className={styles.description}>{description}</div> : null }
      </div>
    );
  };

  renderThumbnails() {
    const data = [...this.props.images, ...(this.props.videos || []).map((video) => ({ ...video, isVideo: true }))];

    return (data.length > 1) && data.map((img, idx) => (
      <div
        key={img.thumbnail}
        className={cx(
          styles.thumbnail,
          { [styles.active]: this.state.currentImage === idx }
        )}
        onClick={() => this.handleThumbnailClick(idx)}
      >
        {!img.raw
          ? <img alt="" src={img.thumbnail} key={img.thumbnail} />
          : <img alt="" src={iconPdf} key={img.thumbnail} />
        }
        {img.isVideo && (<div className={styles.playIcon} />)}
      </div>));
  }

  renderGallery = () => {
    const { isMobile, isRtl } = this.props;
    const images = get(this.props, 'images') || [];
    const videos = get(this.props, 'videos') || [];

    const gallerySwiperParams = getGallerySwiperParams({
      title: {
        nextParams: `${this.props.translate('nextBtn')}`,
        prevParams: `${this.props.translate('prevBtn')}`
      },
      className: 'LightboxArrow',
      isMobile,
      isRtl
    });

    /* eslint-disable jsx-a11y/media-has-caption */
    return (
      <div className={cx(styles.imgContainer, { [styles.pageZoomed]: this.props.isPageZoomed })}>
        <CloseButton
          onClose={this.props.hideLightbox}
          title={`${this.props.translate('closeBtn')}`}
          isMobile={this.props.isMobile}
        />

        <Swiper
          {...gallerySwiperParams}
          zoom={isMobile ? { maxRatio: 3 } : undefined}
          on={{ slideChange: this.slideChange, slideChangeTransitionStart: this.resetZoom }}
          ref={(ref) => { this.swiperRef = ref; }}
          initialSlide={this.state.currentImage}
          noSwiping={images.length <= 1}
        >
          {images.map((image) => {
            const { full, name, raw } = image;

            if (raw) {
              return (
                <div className={cx(styles.image, styles.rawWrapper)} key={full}>
                  <img src={iconPdf} alt="" />
                  <a href={full}>{name}</a>
                </div>
              );
            }

            if (!isImagePosterReady(image)) {
              return (
                <div className={styles.image} key={full || image.thumbnail}>
                  <ImageStatusBadge status={image.status} error={image.error} />
                </div>
              );
            }

            return (
              <div className={styles.image} key={full}>
                <div className="swiper-zoom-container">
                  <img src={full} alt="" />
                </div>
              </div>
            );
          })}

          {videos.map((video) => {
            const { thumbnail, full } = video;

            if (!isVideoPosterReady(video)) {
              return (
                <div className={styles.image} key={video.id || thumbnail}>
                  <VideoStatusBadge status={video.status} error={video.error} />
                </div>
              );
            }

            return (
              <div className={styles.image} key={thumbnail}>
                <video
                  controls
                  poster={thumbnail}
                >
                  {getVideoSourceFormats(video).map(format => (
                    <source key={format} src={`${full}.${format}`} type={`video/${format}`} />
                  ))}
                </video>
              </div>
            );
          })}
        </Swiper>

        {this.renderPagination()}

        <div
          className={cx(styles.thumbnailWrapper, (this.props.isRtl)
            ? styles.directionRightToLeft
            : styles.directionLeftToRight)}
        >
          <div
            className={styles.thumbnailRow}
            style={{ width: this.thumbnailsLength }}
            ref={(scrollableAria) => { this.scrollableAria = scrollableAria; }}
          >
            { this.renderThumbnails() }
          </div>
        </div>
        { this.props.additionalButton }
      </div>
    );
  };

  render() {
    const { isOpen } = this.props;
    return (
      <div>
        <Modal
          isOpen={isOpen}
          onRequestClose={this.props.hideLightbox}
          className="lightboxModal"
          bodyOpenClassName="omLightboxOpen"
          isMobile={this.props.isMobile}
          isCloseButtonInvisible
          isRtl={this.props.isRtl}
        >
          { isOpen && this.renderGallery() }
        </Modal>
      </div>
    );
  }
}

OMLightbox.propTypes = {
  isPageZoomed: PropTypes.bool,
  isOpen: PropTypes.bool,
  images: PropTypes.array,
  videos: PropTypes.array,
  currentImage: PropTypes.number,
  hideLightbox: PropTypes.func,
  changeLightboxActiveSlide: PropTypes.func,
  translate: PropTypes.func,
  isRtl: PropTypes.bool,
  isMobile: PropTypes.bool,
  additionalButton: PropTypes.element
};

export default withPageZoom(OMLightbox);
